/**
 * ContributionRequestDialog — iter231
 *
 * Under Model 1 lockdown, group admins cannot directly top up their group's
 * fund. Instead they raise an intent-to-pay request that goes to Kalpesh's
 * pending queue. Kalpesh physically receives the cash, taps "Confirm receipt"
 * on his Admin Wallet page, and the atomic wallet+bridge fires.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, X, Info } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const METHODS = [
  { id: 'cash', label: 'Cash' },
  { id: 'gpay', label: 'GPay / UPI' },
  { id: 'imps', label: 'IMPS / NEFT' },
  { id: 'cheque', label: 'Cheque' },
  { id: 'other', label: 'Other' },
];

export default function ContributionRequestDialog({ open, group, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingMine, setPendingMine] = useState([]);

  useEffect(() => {
    if (!open) return;
    setAmount(''); setMethod('cash'); setNote('');
    // Show any existing pending requests I have for this group
    api.get('/contribution-requests?status=pending')
      .then((r) => setPendingMine((r.data?.requests || []).filter((x) => x.group_id === group?.id)))
      .catch(() => setPendingMine([]));
  }, [open, group?.id]);

  const submit = async () => {
    if (!Number(amount) || Number(amount) <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    setSaving(true);
    try {
      await api.post('/contribution-requests', {
        group_id: group.id,
        amount: Number(amount),
        method,
        note: note?.trim() || null,
      });
      toast.success('Request sent to super admin');
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send request');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (reqId) => {
    if (!window.confirm('Cancel this pending contribution request?')) return;
    try {
      await api.post(`/contribution-requests/${reqId}/cancel`);
      setPendingMine((rows) => rows.filter((r) => r.id !== reqId));
      toast.success('Cancelled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="contribution-request-dialog">
        <DialogHeader>
          <DialogTitle>Request Contribution</DialogTitle>
          <DialogDescription>
            Cash flows through the super admin. Log what you want to contribute — Kalpesh will confirm receipt.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-primary/5 border border-primary/20 rounded-lg p-2.5 flex gap-2 text-xs" data-testid="contrib-req-info">
          <Info className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
          <p>
            Hand ₹cash / GPay to <strong>Kalpesh</strong>. He confirms on his Admin Wallet — money then lands in <strong>{group?.name}</strong>&apos;s fund and credits your coin balance.
          </p>
        </div>

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Amount (₹)</Label>
            <Input
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              data-testid="contrib-req-amount"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">How will you pay?</Label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {METHODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMethod(m.id)}
                  className={`p-2 border-2 rounded-lg text-xs font-semibold ${
                    method === m.id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                  }`}
                  data-testid={`contrib-req-method-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="contrib-req-note" />
          </div>
        </div>

        {pendingMine.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Your pending requests</p>
            <div className="space-y-1">
              {pendingMine.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-xs bg-muted/40 rounded px-2 py-1.5" data-testid={`contrib-req-pending-${r.id}`}>
                  <span>₹{Number(r.amount).toLocaleString('en-IN')} · {r.method}</span>
                  <Button size="sm" variant="ghost" className="h-6 text-red-500" onClick={() => cancel(r.id)}>
                    Cancel
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-3 border-t pt-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            <X className="w-3.5 h-3.5 mr-1" /> Close
          </Button>
          <Button onClick={submit} disabled={saving || !amount} data-testid="contrib-req-submit">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            Send Request
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
